import React from "react";
import { Clock, CheckCircle, XCircle } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const CampaignRequest = ({ campaigns = [], navigate, onViewAll }) => {
  const { isDarkMode } = useTheme();

  const pendingCampaigns = campaigns.filter(
    (campaign) =>
      campaign.approvalStatus?.toLowerCase() === "pending" ||
      campaign.status?.toLowerCase() === "pending"
  );

  const getStatusIcon = (status) => {
    const statusLower = status?.toLowerCase();
    const pendingColor = isDarkMode ? "text-orange-400" : "text-orange-600";
    const approvedColor = isDarkMode ? "text-green-400" : "text-green-600";
    const rejectedColor = isDarkMode ? "text-red-400" : "text-red-600";
    const defaultColor = isDarkMode ? "text-gray-400" : "text-gray-500";

    switch (statusLower) {
      case "pending":
        return <Clock size={16} className={pendingColor} />;
      case "approved":
        return <CheckCircle size={16} className={approvedColor} />;
      case "rejected":
        return <XCircle size={16} className={rejectedColor} />;
      default:
        return <Clock size={16} className={defaultColor} />;
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    if (isDarkMode) {
      switch (statusLower) {
        case "pending":
          return "bg-orange-900 text-orange-300";
        case "approved":
          return "bg-green-900 text-green-300";
        case "rejected":
          return "bg-red-900 text-red-300";
        default:
          return "bg-gray-900 text-gray-300";
      }
    } else {
      switch (statusLower) {
        case "pending":
          return "bg-orange-100 text-orange-700";
        case "approved":
          return "bg-green-100 text-green-700";
        case "rejected":
          return "bg-red-100 text-red-700";
        default:
          return "bg-gray-100 text-gray-700";
      }
    }
  };

  const handleReview = (campaignId) => {
    navigate(`/campaign/${campaignId}`, { state: { from: "homepage" } });
  };

  const handleViewAll = () => {
    if (onViewAll) {
      onViewAll();
    } else {
      navigate("/campaigns");
    }
  };

  const containerBg = isDarkMode ? "bg-black" : "bg-white";
  const containerBorder = isDarkMode ? "border-zinc-900" : "border-gray-200";
  const headerText = isDarkMode ? "text-white" : "text-gray-900";
  const tableHeader = isDarkMode ? "text-gray-500" : "text-gray-500";
  const tableRowText = isDarkMode ? "text-white" : "text-gray-800";
  const tableDetailText = isDarkMode ? "text-gray-500" : "text-gray-500";
  const rowBorder = isDarkMode ? "border-zinc-900" : "border-gray-200";
  const rowHoverBg = isDarkMode ? "hover:bg-zinc-900" : "hover:bg-gray-50";
  const accentColor = isDarkMode
    ? "text-orange-400 hover:text-orange-300"
    : "text-blue-600 hover:text-blue-800";

  return (
    <div
      className={isDarkMode ? "bg-black text-white" : "bg-white text-gray-900"}
    >
      <div
        className={`${containerBg} border ${containerBorder} rounded-xl p-5 h-full shadow-lg`}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className={`text-xl font-semibold ${headerText}`}>
            Campaign Requests
          </h3>
          <button
            onClick={handleViewAll}
            className={`text-sm font-medium transition-colors ${accentColor}`}
          >
            View All →
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={`border-b ${rowBorder}`}>
                <th
                  className={`text-left ${tableHeader} text-sm font-medium pb-3`}
                >
                  Campaign Name
                </th>
                <th
                  className={`text-left ${tableHeader} text-sm font-medium pb-3`}
                >
                  Merchant
                </th>
                <th
                  className={`text-left ${tableHeader} text-sm font-medium pb-3`}
                >
                  Budget
                </th>
                <th
                  className={`text-left ${tableHeader} text-sm font-medium pb-3`}
                >
                  Date
                </th>
                <th
                  className={`text-left ${tableHeader} text-sm font-medium pb-3`}
                >
                  Status
                </th>
                <th
                  className={`text-left ${tableHeader} text-sm font-medium pb-3`}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {pendingCampaigns.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className={`text-center py-10 ${tableDetailText}`}
                  >
                    No pending campaign requests
                  </td>
                </tr>
              ) : (
                pendingCampaigns.map((campaign) => (
                  <tr
                    key={campaign._id}
                    className={`${rowBorder} border-b ${rowHoverBg} transition-colors`}
                  >
                    <td className={`py-3 ${tableRowText}`}>{campaign.title}</td>
                    <td className={`py-3 ${tableRowText}`}>
                      {campaign.merchantId?.firstName}{" "}
                      {campaign.merchantId?.lastName}
                    </td>
                    <td className={`py-3 ${tableRowText}`}>
                      ${campaign.budget?.toLocaleString() || "0"}
                    </td>
                    <td className={`py-3 ${tableDetailText} text-sm`}>
                      {campaign.createdAt
                        ? new Date(campaign.createdAt).toLocaleDateString()
                        : "N/A"}
                    </td>
                    <td className="py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${getStatusColor(
                          campaign.approvalStatus || campaign.status
                        )}`}
                      >
                        {getStatusIcon(
                          campaign.approvalStatus || campaign.status
                        )}
                        {campaign.approvalStatus || campaign.status}
                      </span>
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => handleReview(campaign._id)}
                        className={`text-sm font-medium transition-colors ${accentColor}`}
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CampaignRequest;
